import { useSelector } from "react-redux";
import { matchPath, useLocation } from "react-router-dom";
import { call, select } from "redux-saga/effects";

import {
  FeatureGates,
  getFeatureGates,
  getLocaleWithScript,
  RouteConfig,
  RouteConfigMatch,
  RouteParameters,
} from "@gamedev-web/gamedev-sdk-core";
import { ERT } from "@gaming/extract-redux-saga-return-type";

import { BaseRootState } from "../redux/state/state";
import { evaluateRouteConstraint } from "./routeConstraint";

let registeredRoutes: RouteConfig[] = [];

/**
 * Updates the list of registered routes to be used for route matching and
 * lookup
 * @description
 *  NOTE: ONLY FOR PLATFORM USE.
 *  🛑 DO NOT USE IN PARTNER CODE OR BAD THINGS WILL HAPPEN. YOU HAVE BEEN WARNED!! 👿
 * @param routes
 */
export const updateRegisteredRoutes = (routes: RouteConfig[]) => (registeredRoutes = routes);

export const getMatchedRouteConfig = <TRootState extends BaseRootState = BaseRootState>(
  pathToMatch: string,
  featureGates?: FeatureGates,
  locale?: string
): RouteConfigMatch<TRootState> | undefined => {
  if (registeredRoutes.length === 0) {
    throw new Error("No routes have been registered. Invalid use of getMatchedRouteConfig.");
  }

  for (const routeConfig of registeredRoutes) {
    if (evaluateRouteConstraint(routeConfig.constraint, featureGates, locale)) {
      const routeMatch = matchPath<RouteParameters>(pathToMatch, routeConfig);
      if (routeMatch) {
        return {
          ...routeConfig,
          match: routeMatch,
        };
      }
    }
  }

  return;
};

export const getRoutes = () => registeredRoutes;

export const useMatchedRouteConfig = <TRootState extends BaseRootState = BaseRootState>(
  pathToMatch?: string
): RouteConfigMatch<TRootState> | undefined => {
  const featureGates = useSelector(getFeatureGates);
  const currentLocation = useLocation();
  const locale = useSelector(getLocaleWithScript);
  const path = pathToMatch || currentLocation.pathname;

  return getMatchedRouteConfig(path, featureGates, locale);
};

function* _getMatchedRouteConfigSagaEffect(pathToMatch: string) {
  const featureGates: ERT<typeof getFeatureGates> = yield select(getFeatureGates);
  const locale: ERT<typeof getLocaleWithScript> = yield select(getLocaleWithScript);
  const result: ERT<typeof getMatchedRouteConfig> = yield call(
    getMatchedRouteConfig,
    pathToMatch,
    featureGates,
    locale
  );

  return result;
}

export function getMatchedRouteConfigSagaEffect(pathToMatch: string) {
  return call(_getMatchedRouteConfigSagaEffect, pathToMatch);
}
